import React, { useEffect, Fragment } from 'react';
import { isEmpty } from 'lodash';
import { BaseTemplate, PageHead, Card } from '@myob/myob-widgets';
import ProductTitle from './components/ProductTitle';
import AlertBanner from './components/AlertBanner';
import BusinessName from './components/BusinessName';
import Status from './components/Status';
import Actions from './components/Actions';
import Cancellation from './components/Cancellation';
import Footer from './components/Footer';
import ErrorComponent from './components/ErrorComponent';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { RootState } from 'stores';
import { IProductsState, ICheckoutState, ProductManagementContext } from './reducers/type';
import { fetchProductList } from './reducers/products';
import { trackViewInProductManagement } from './helper';
import LoadingWrapper from 'components/LoadingWrapper';
import { SectionWrapper, ProductPropertiesWrapper } from './style';
import { getFeatureToggle, ToggleNames } from 'helpers/featureToggle';
import NavigationBar from 'components/NavigationBar';
import { ERoutePath } from 'router';
import { getProductManagementContext } from 'modules/ProductManagement/reducers/accessors';
import { getCheckout, getProducts } from './reducers/accessors';

const ProductManagement: React.FC = () => {
    const dispatch = useDispatch();
    const { isLoading, isError: isProductsError, error, productList } = useSelector<RootState, IProductsState>(
        getProducts,
        shallowEqual,
    );
    useEffect(() => {
        isEmpty(productList) && dispatch(fetchProductList());
    }, [dispatch, productList]);

    const { region, subscriptionStatus } = useSelector<RootState, ProductManagementContext>(
        getProductManagementContext,
        shallowEqual,
    );

    const { isError: isCheckoutError } = useSelector<RootState, ICheckoutState>(getCheckout, shallowEqual);
    useEffect(() => {
        const productManagementContext = { region, subscriptionStatus };
        !isProductsError && !isLoading && trackViewInProductManagement(productManagementContext, 'granted');
        isProductsError && trackViewInProductManagement(productManagementContext, 'denied', error?.message);
    }, [isLoading, isProductsError, error, region, subscriptionStatus]);

    return (
        <Fragment>
            <NavigationBar url={`#${ERoutePath.ProductManagement}`} />
            <LoadingWrapper isLoading={isLoading}>
                <main data-testid="productManagement">
                    <BaseTemplate>
                        <PageHead title="Manage my product" />
                        {productList.length === 0 || isProductsError || isCheckoutError ? (
                            <ErrorComponent />
                        ) : (
                            <>
                                <Card>
                                    <ProductTitle />
                                    {getFeatureToggle(ToggleNames.FEATURE_CHECKOUT_ACTIONS) && <AlertBanner />}
                                    <ProductPropertiesWrapper>
                                        <SectionWrapper>
                                            <BusinessName />
                                            <Status />
                                            <Actions />
                                        </SectionWrapper>
                                        <hr />
                                        {getFeatureToggle(ToggleNames.FEATURE_SSP_ACTIONS) && <Cancellation />}
                                    </ProductPropertiesWrapper>
                                </Card>
                                <Footer />
                            </>
                        )}
                    </BaseTemplate>
                </main>
            </LoadingWrapper>
        </Fragment>
    );
};

export default ProductManagement;
